import { useId } from 'react'

import { Card, Pill } from '../ui'
import type { ComparisonResult } from '../../lib/api'
import { cornerWords, type HomeDisplayView } from '../../lib/home'
import { comparisonMapIsDrawable, mapFrame } from '../../lib/stateGeometry'

/** EXP-004 C2 / EXP-005-C1: only the package supplies confirmed-baseline ring geometry. */
export function ComparisonMap({ result, beforeTime, afterTime, display }: {
  result: ComparisonResult; beforeTime: string; afterTime: string; display: HomeDisplayView | null
}) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const before = result.points?.recordings?.find((point) => point.recording_id === result.before_recording_id)?.state_map
  const after = result.points?.recordings?.find((point) => point.recording_id === result.after_recording_id)?.state_map
  if (before === undefined || after === undefined) return null
  const coordinates = typeof before.activation === 'number' && typeof before.balance === 'number'
    && typeof after.activation === 'number' && typeof after.balance === 'number'
    ? { beforeActivation: before.activation, beforeBalance: before.balance, afterActivation: after.activation, afterBalance: after.balance }
    : null
  const beforeWord = before.corner_word !== null && before.corner_word.trim() !== '' ? before.corner_word : null
  const afterWord = after.corner_word !== null && after.corner_word.trim() !== '' ? after.corner_word : null
  const usual = result.points?.usual ?? null
  const description = `${beforeWord === null ? beforeTime : `${beforeTime}: ${beforeWord}`}. ${afterWord === null ? afterTime : `${afterTime}: ${afterWord}`}.${usual !== null && display !== null ? ` ${display.usual}.` : ''}`
  const drawable = comparisonMapIsDrawable(result, display)
  const research = before.validated === false || after.validated === false
  if (!drawable) {
    if (beforeWord === null && afterWord === null) return null
    return <section className="comparison__position-summary" aria-label="Where the two readings sit">
      {[
        { key: 'before', time: beforeTime, word: beforeWord },
        { key: 'after', time: afterTime, word: afterWord },
      ].filter((position) => position.word !== null).map((position) => <div className="comparison__position-summary-item" key={position.key}>
        <time className="comparison__time">{position.time}</time><span>{position.word}</span>
      </div>)}
      {research && <Pill tone="purple">Research</Pill>}
    </section>
  }
  const frame = display === null ? null : mapFrame(320, 320, display.axisClip)
  const corners = display === null ? null : cornerWords(display.quadrants)
  const ellipse = frame !== null && usual !== null ? frame.ellipse(usual) : null
  return <Card as="section" className="comparison__map-card" aria-label="Where the two readings sit">
    <div className="comparison__map-head"><h2 className="comparison__label">Where the two readings sit</h2>
      {research && <Pill tone="purple">Research</Pill>}
    </div>
    <div className="comparison__map-body">
      {frame !== null && coordinates !== null && drawable && <div className="comparison__map" data-testid="comparison-map">
        <svg viewBox="0 0 320 320" role="img" aria-label={description}>
          <defs>
            <linearGradient id={`${uid}top`}><stop offset="0%" className="comparison__field--tl" /><stop offset="100%" className="comparison__field--tr" /></linearGradient>
            <linearGradient id={`${uid}bottom`}><stop offset="0%" className="comparison__field--bl" /><stop offset="100%" className="comparison__field--br" /></linearGradient>
            <linearGradient id={`${uid}fade`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="white" stopOpacity="0" /><stop offset="100%" stopColor="white" stopOpacity="1" /></linearGradient>
            <mask id={`${uid}mask`}><rect x={frame.padX} y={frame.padY} width={frame.pw} height={frame.ph} fill={`url(#${uid}fade)`} /></mask>
            <marker id={`${uid}arrow`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" className="comparison__arrow-tip" />
            </marker>
          </defs>
          <rect className="comparison__field" x={frame.padX} y={frame.padY} width={frame.pw} height={frame.ph} rx="16" fill={`url(#${uid}top)`} />
          <rect className="comparison__field" x={frame.padX} y={frame.padY} width={frame.pw} height={frame.ph} rx="16" fill={`url(#${uid}bottom)`} mask={`url(#${uid}mask)`} />
          {ellipse !== null && <ellipse className="bp-ring comparison__usual" {...ellipse} data-testid="comparison-usual" />}
          <line className="comparison__axis" x1={frame.padX} y1={frame.y(0)} x2={frame.right} y2={frame.y(0)} />
          <line className="comparison__axis" x1={frame.x(0)} y1={frame.padY} x2={frame.x(0)} y2={frame.bottom} />
          <line className="comparison__arrow" x1={frame.x(coordinates.beforeBalance)} y1={frame.y(coordinates.beforeActivation)}
            x2={frame.x(coordinates.afterBalance)} y2={frame.y(coordinates.afterActivation)} markerEnd={`url(#${uid}arrow)`} />
          <circle className="comparison__dot comparison__dot--before" cx={frame.x(coordinates.beforeBalance)} cy={frame.y(coordinates.beforeActivation)} r="7" />
          <circle className="comparison__dot comparison__dot--after" cx={frame.x(coordinates.afterBalance)} cy={frame.y(coordinates.afterActivation)} r="7" />
        </svg>
        {corners !== null && <div className="comparison__corners" aria-hidden="true">
          <span>{corners.tl}</span><span>{corners.tr}</span><span>{corners.bl}</span><span>{corners.br}</span>
        </div>}
      </div>}
      <div className="comparison__map-legend">
        <div>{beforeWord !== null && <Pill>{beforeWord}</Pill>}<span className="comparison__time">{beforeTime}</span></div>
        <div>{afterWord !== null && <Pill tone="cyan">{afterWord}</Pill>}<span className="comparison__time">{afterTime}</span></div>
        {research && <Pill tone="purple" className="comparison__phone">Research</Pill>}
      </div>
    </div>
  </Card>
}
